import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import type { SearchParser, SearchSerializer } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "./index.css";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { queryClient } from "./lib/query-client";
import { refreshSession, type RefreshResult } from "./lib/axios";

// Keep list-valued search params readable as repeated keys
// (?status=todo&status=pending) instead of the default JSON encoding
// (?status=%5B%22todo%22...%5D). Scalars keep the default JSON round-trip
// (numbers/booleans parse back to their real types).
const stringifySearch: SearchSerializer = (search) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined) continue;
    for (const v of Array.isArray(value) ? value : [value]) {
      const asString = z.string().safeParse(v);
      params.append(key, asString.success ? asString.data : JSON.stringify(v));
    }
  }
  const str = params.toString();
  return str ? `?${str}` : "";
};

const parseSearch: SearchParser = (searchStr) => {
  const params = new URLSearchParams(searchStr);
  const entries = [...new Set(params.keys())].map((key) => {
    const values = params.getAll(key).map((v) => {
      try {
        return JSON.parse(v);
      } catch {
        return v;
      }
    });
    return [key, values.length > 1 ? values : values[0]] as const;
  });
  return Object.fromEntries(entries);
};

// Create a new router instance
const router = createRouter({ routeTree, parseSearch, stringifySearch });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app. The access token lives in memory, so a reload starts without
// one: trade the refresh cookie for it first, so the route guards' synchronous
// `auth.getToken()` checks see the restored session. No session → null → the
// guards send the visitor to /login as before.
// A refresh that gets no verdict (server restarting, network blip) is retried
// a couple of times before giving up, so a deploy doesn't bounce everyone to
// the login page (T-234). A 401 is final immediately.
async function restoreSession(): Promise<RefreshResult> {
  let result = await refreshSession();
  for (const wait of [1000, 3000]) {
    if (result.kind !== "unavailable") break;
    await new Promise((resolve) => setTimeout(resolve, wait));
    result = await refreshSession();
  }
  return result;
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  void restoreSession()
    .catch((): RefreshResult => ({ kind: "unavailable" }))
    .then((result) => {
      // Still no verdict after the retries: the guards will show the login
      // page, so say the server -- not the session -- was the problem (T-245).
      if (result.kind === "unavailable") {
        toast.error(
          "Couldn't reach the server to restore your session. Reload in a moment, or sign in again.",
          { id: "session-unavailable" },
        );
      }
      const root = ReactDOM.createRoot(rootElement);
      root.render(
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> */}
        </QueryClientProvider>
      );
    });
}
